import React, { useState, useRef, useEffect } from "react";
import moment from "moment";
import PropTypes from "prop-types";
import { useFormContext, Controller } from "react-hook-form";

import { Stack } from "@mui/material";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Button from "components/common/Button";
import EventIcon from "@mui/icons-material/Event";
import InputAdornment from "@mui/material/InputAdornment";

import "./DateRangeSelect.scss";

function DateRangeSelect({
  name,
  label,
  helperText,
  isOptional,
  placeholder = "Select",
  value: propValue,
  onChange: propOnChange,
  error: propError,
  size = "small",
  variant = "outlined",
  disabled = false,
  fontSize = "",
  inputClassName = "",
  dateFormat = "MMM D, YYYY",
  labelClass = "",
  dateRangeComponent,
  dateRangeProps = {},
  ...other
}) {
  const formContext = useFormContext();
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState(null);
  const [tempRange, setTempRange] = useState(null);

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempRange(selectedRange);
    handleClose();
  };

  const handleDone = () => {
    isUserInteractionRef.current = true;
    setSelectedRange(tempRange);
    handleClose();
  };

  const formatDateRange = (range) => {
    if (!range || !range.startDate || !range.endDate) {
      return placeholder;
    }

    const startDate = moment(range.startDate).format(dateFormat);
    const endDate = moment(range.endDate).format(dateFormat);

    if (moment(range.startDate).isSame(range.endDate, "day")) {
      return startDate;
    }

    return `${startDate} - ${endDate}`;
  };

  const handleDateRangeChange = (selection, dateLabels, daysCount) => {
    setTempRange({
      startDate: selection.startDate,
      endDate: selection.endDate,
      dateLabels,
      daysCount,
    });
  };

  const handleChangeRef = useRef(null);
  const isUserInteractionRef = useRef(false);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (selectedRange && handleChangeRef.current && isUserInteractionRef.current) {
      handleChangeRef.current(selectedRange);
      isUserInteractionRef.current = false;
    }
  }, [selectedRange]);

  useEffect(() => {
    if (propValue && JSON.stringify(propValue) !== JSON.stringify(selectedRange)) {
      isUserInteractionRef.current = false;
      setSelectedRange(propValue);
      setTempRange(propValue);
    }
  }, [propValue, selectedRange]);

  const renderDateRangeSelect = (field = {}, fieldError) => {
    const currentValue = field.value || propValue || null;

    if (currentValue && !selectedRange) {
      setTimeout(() => {
        setSelectedRange(currentValue);
        setTempRange(currentValue);
      }, 0);
    }

    const handleChange = (value) => {
      if (field.onChange) {
        field.onChange(value);
      }

      if (propOnChange) {
        propOnChange(value);
      }
    };

    handleChangeRef.current = handleChange;

    return (
      <div className="date-range-select" ref={containerRef}>
        <FormControl fullWidth data-testid="form-control">
          <InputLabel shrink htmlFor={name} className={`date-range-select__calendar-label ${labelClass}`}>
            {`${label} ${isOptional ? "(optional)" : ""}`}
          </InputLabel>
          <div className="ant-text-component mui-theme-border-colors">
            <TextField
              id={name}
              name={name}
              onClick={handleOpen}
              disabled={disabled}
              error={!!fieldError}
              helperText={fieldError ? fieldError.message : helperText}
              value={formatDateRange(selectedRange)}
              size={size}
              variant={variant}
              slotProps={{
                input: {
                classes: {
                  root: `custom-text-input-root text-left text-field-padding text-field-input-${size} ${inputClassName} ${disabled ? "disabled-input" : ""}`,
                  input: `para-body-l-regular-important flex ${fontSize} ${formatDateRange(selectedRange) === placeholder ? "date-range-select__placeholder" : ""}`,
                  focused: "custom-text-input-focused",
                  error: "custom-text-input-error",
                },
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <EventIcon onClick={handleOpen} className="cursor-pointer color-icon-secondary" />
                  </InputAdornment>
                ),
                "data-testid": "date-range-select-field",
              },
                inputLabel: {
                shrink: true,
              },
                formHelperText: {
                classes: {
                  root: "para-body-s-regular error-text-color ml-0",
                },
              },
              }}


              {...other}
            />
          </div>
          {isOpen && (
            <div className="date-range-select__calendar">
              <div className="date-range-select__popup">
                {dateRangeComponent ? (
                  React.cloneElement(dateRangeComponent, {
                    onChange: handleDateRangeChange,
                    ...dateRangeProps,
                  })
                ) : (
                  <div>Please provide a dateRangeComponent</div>
                )}

                <Stack
                  direction="row"
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                  <div className="request-timeoff-indicators-container ml-1">
                    <div className="request-timeoff-indicator-box-label">
                      <span className="request-timeoff-today-box-indicator" />
                      <span className="title-sm overline">TODAY</span>
                    </div>

                    <div className="request-timeoff-indicator-box-label">
                      <span className="request-timeoff-selected-box-indicator" />
                      <span className="title-sm overline">SELECTED</span>
                    </div>
                  </div>

                  <div className="flex gap-05 mr-1" style={{ marginBottom: "0px !important" }}>
                    <span className="align-self-center calendar-dot-indicator-orange" />
                    <p className="body-md align-self-center paragraph">Leaves</p>
                    <span className="align-self-center calendar-dot-indicator-green" />
                    <p className="body-md align-self-center paragraph">Holidays</p>
                  </div>
                </Stack>

                <div className="date-range-select__footer-actions">
                  <Button variant="text" onClick={handleCancel} label="Cancel" />
                  <Button variant="text" onClick={handleDone} label="Done" />
                </div>
              </div>
            </div>
          )}
        </FormControl>
      </div>
    );
  };

  return formContext && !propValue && !propOnChange ? (
    <Controller
      name={name}
      control={formContext.control}
      defaultValue=""
      render={({ field, fieldState: { error } }) => renderDateRangeSelect(field, error)}
    />
  ) : (
    renderDateRangeSelect({ value: propValue, onChange: propOnChange }, propError ? { message: helperText } : null)
  );
}

DateRangeSelect.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  helperText: PropTypes.string,
  isOptional: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
  }),
  onChange: PropTypes.func,
  error: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium"]),
  variant: PropTypes.oneOf(["standard", "outlined", "filled"]),
  disabled: PropTypes.bool,
  fontSize: PropTypes.string,
  inputClassName: PropTypes.string,
  dateFormat: PropTypes.string,
  labelClass: PropTypes.string,
  dateRangeComponent: PropTypes.element.isRequired,
  dateRangeProps: PropTypes.shape({}),
};

DateRangeSelect.defaultProps = {
  helperText: "",
  placeholder: "Select",
  value: null,
  onChange: undefined,
  error: false,
  size: "small",
  variant: "outlined",
  disabled: false,
  isOptional: false,
  fontSize: "font-regular",
  inputClassName: "",
  dateFormat: "MMM D, YYYY",
  labelClass: "",
  dateRangeProps: {},
};

export default DateRangeSelect;
